import { useEffect, useRef, type RefObject } from 'react'
import { locationPermissionGranted, readWarmPosition } from '@/lib/clockPosition'
import type { ClockLocation } from '@/hooks/useTimesheet'

/**
 * EP-154: keeps the latest quiet position fix of the phone, so the clock tap
 * need not wait for a slow read. It reads on mount and each time the app
 * comes back to the foreground, and only when the location permission is
 * already granted: it never prompts, and a refused or unknown permission
 * leaves the ref empty. Failures are silent (the tap-time read still runs).
 */
export function useWarmPosition(): RefObject<ClockLocation | null> {
  const warm = useRef<ClockLocation | null>(null)
  useEffect(() => {
    let alive = true
    let reading = false
    const refresh = async () => {
      if (reading) return
      reading = true
      try {
        if (!(await locationPermissionGranted())) return
        const fix = await readWarmPosition()
        if (alive) warm.current = fix
      } catch {
        // Nothing warm; the tap starts its own read.
      } finally {
        reading = false
      }
    }
    const onForeground = () => {
      if (document.visibilityState === 'visible') void refresh()
    }
    void refresh()
    document.addEventListener('visibilitychange', onForeground)
    window.addEventListener('focus', onForeground)
    return () => {
      alive = false
      document.removeEventListener('visibilitychange', onForeground)
      window.removeEventListener('focus', onForeground)
    }
  }, [])
  return warm
}
